import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { IBaseResponse } from '@carm/_utils/models';
import { HttpParams } from '@angular/common/http';
import * as dayjs from 'dayjs';
import { dateFormat } from '@carm/shared/constants/const-carm';
import { LoaderSpinnerTypes } from '@carm/_core';
import { IGetDraftsResponse, IRoiDraftResponse, IRoiCreateDraftRequest, IRoiUpdateDraftRequest, IRoiChange, IRoiChangeResponse, IRoiInvoiceResponse, IRoiChangeUpdateResponse } from '@carm/_core/declarations/models/api';
import { mapListOfInvoices, mapListOfCommodities } from '@carm/_core/declarations/models/api/roi.mapper';
import { IRoiState } from '../models/form/roi-state.model';

@Injectable()
export class RoiDraftService extends BaseApiService {


  private readonly roiStateSubject = new BehaviorSubject<IRoiState>({ selectedRoiInvoices: [], selectedRoiInvoicesByCommodity: []});

  constructor(protected injector: Injector) {
    super(injector);
  }

  public get onRoiStateChange$(): Observable<IRoiState> {
    return this.roiStateSubject.asObservable();
  }

  public updateSelectedInvoicesState(invoices: number[]){
    this.roiStateSubject.next({...this.roiStateSubject.value, selectedRoiInvoices: invoices});
  }

  public updateSelectedInvoicesByCommodityState(invoices: number[]){
    this.roiStateSubject.next({...this.roiStateSubject.value, selectedRoiInvoicesByCommodity: invoices});
  }

  get cadRoiDraftUrl(): string {
    return `${this.endPointBP}/cad/roi/draft`;
  }

  loadStateFromDraftUiContent(inObj: {draftUiContent?: string }){
    if (inObj && inObj.draftUiContent){
      let roiState:IRoiState = JSON.parse(inObj.draftUiContent) as IRoiState;
      this.roiStateSubject.next(roiState);
    }
  }

  resetROIState(){
     this.roiStateSubject.next({selectedRoiInvoices: []});
  }

  getRoiDrafts(
    minDate: Date,
    maxDate: Date,
    currentPage: number,
    pageSize: number,
    sortBy: string,
    filterField: string,
    filterValue: string
  ): Observable<IGetDraftsResponse> {
    const endPoint = `${this.cadRoiDraftUrl}`;
    if (minDate && maxDate) {
      const params = new HttpParams()
        .set('startDate', dayjs(minDate).format(dateFormat))
        .set('endDate', dayjs(maxDate).format(dateFormat))
        .set('pageSize', pageSize.toString())
        .set('currentPage', currentPage.toString())
        .set('sort', sortBy)
        .set('filterField', filterField)
        .set('filterValue', filterValue);
      return this.httpAuthGet<IGetDraftsResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
    } else {
      return this.httpAuthGet<IGetDraftsResponse>(endPoint);
    }
  }

  getDraft(draftUid: string): Observable<IRoiDraftResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}`;
    return this.httpAuthGet<IRoiDraftResponse>(endPoint).pipe(
      tap(draft=> this.loadStateFromDraftUiContent(draft)));
  }

  getDraftByTransactionNumber(transactionNumber: string): Observable<IRoiDraftResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/transaction/${transactionNumber}`;
    return this.httpAuthGet<IRoiDraftResponse>(endPoint).pipe(
      tap(draft=> this.loadStateFromDraftUiContent(draft)));
  }

  createDraft(roiData: IRoiCreateDraftRequest): Observable<IRoiDraftResponse> {
    const endPoint = `${this.cadRoiDraftUrl}`;
    return this.httpAuthPost<IRoiDraftResponse>(endPoint, roiData);
  }

  updateDraft(draftUid: string, roiData: IRoiUpdateDraftRequest): Observable<IRoiDraftResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}`;
    roiData.draftUiContent = JSON.stringify(this.roiStateSubject.value);
    return this.httpAuthPut<IRoiDraftResponse>(endPoint, roiData);
  }


  updateDraftUiContent(draftUid: string): Observable<IRoiDraftResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/draftContent`;
    let payload = {
      draftUiContent: JSON.stringify(this.roiStateSubject.value)
    };
    return this.httpAuthPut<IRoiDraftResponse>(endPoint, payload);
  }

  deleteDraftRoi(draftUid: string, etag: string): Observable<IBaseResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}`;
    return this.httpAuthDelete<IBaseResponse>(endPoint, null, { etag });
  }

  getRoiInvoiceList(
    draftUid: string,
    searchString: string
  ): Observable<IRoiChange[]> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/invoices`;
    const params = new HttpParams().set('searchString', searchString || '');
    return this.httpAuthGet<IRoiChangeResponse>(endPoint, params).pipe(
      map(response => mapListOfInvoices(response))
    );
  }

  getRoiCommoditiesList(
    draftUid: string,
    searchString: string,
    invoiceId: string
  ): Observable<IRoiChange[]> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/invoices/${invoiceId}/goods`;
    const params = new HttpParams().set('searchString', searchString || null);
    return this.httpAuthGet<IRoiChangeResponse>(endPoint, params).pipe(
      map(response => mapListOfCommodities(response))
    );
  }

  addCommodityToRoi(
    draftUid: string,
    invoiceUid: string,
    roiInvoiceRequest: IRoiChange
  ): Observable<IBaseResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/invoices/${invoiceUid}/goods`;
    roiInvoiceRequest.draftUiContent = JSON.stringify(this.roiStateSubject.value);
    return this.httpAuthPost<IBaseResponse>(endPoint, roiInvoiceRequest);
  }

  addInvoiceToRoi(
    draftUid: string,
    roiInvoiceRequest: IRoiChange
  ): Observable<IRoiInvoiceResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/invoices`;
    roiInvoiceRequest.draftUiContent = JSON.stringify(this.roiStateSubject.value);
    return this.httpAuthPost<IRoiInvoiceResponse>(endPoint, roiInvoiceRequest);
  }

  updateCommodity(
    draftUid: string,
    commodityChange: IRoiChange
  ): Observable<IRoiChangeUpdateResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/goods/${commodityChange.uid}`;
    commodityChange.draftUiContent = JSON.stringify(this.roiStateSubject.value);
    return this.httpAuthPut<IRoiChangeUpdateResponse>(endPoint, commodityChange);
  }

  deleteCommodity(
    draftUid: string,
    commodity: IRoiChange
  ): Observable<IBaseResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/goods/${commodity.uid}`;
    commodity.draftUiContent = JSON.stringify(this.roiStateSubject.value);
    return this.httpAuthDelete<IBaseResponse>(endPoint, null, commodity);
  }

  updateInvoice(
    draftUid: string,
    invoiceChange: IRoiChange
  ): Observable<IRoiChangeUpdateResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/invoices/${invoiceChange.uid}`;
    const payload = {
      invoiceLineNo: invoiceChange.invoiceLineNo,
      uid: invoiceChange.uid,
      etag: invoiceChange.etag,
      reasons: invoiceChange.reasons,
      draftUiContent: JSON.stringify(this.roiStateSubject.value)
    };
    return this.httpAuthPut<IRoiChangeUpdateResponse>(endPoint, payload);
  }

  deleteInvoice(
    draftUid: string,
    invoice: IRoiChange
  ): Observable<IBaseResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/invoices/${invoice.uid}`;
    invoice.draftUiContent = JSON.stringify(this.roiStateSubject.value);
    return this.httpAuthDelete<IBaseResponse>(endPoint, null, invoice);
  }

  submitROI(draftUid: string): Observable<IBaseResponse> {
    const endPoint = `${this.cadRoiDraftUrl}/${draftUid}/submit`;
    return this.httpAuthPost<IBaseResponse>(endPoint, null);
  }

  addInvoiceCommodityToRoi(draftUid: string, request: IRoiChange) {
    return this.addInvoiceToRoi(draftUid, {
      invoiceLineNo: request.invoiceLineNo,
    } as IRoiChange).pipe(
      mergeMap(data => {
        return this.addCommodityToRoi(draftUid, data.uid, request);
      })
    );
  }
}
